import { Component, computed, inject, input, model, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NgClass } from "@angular/common";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { map } from "rxjs/operators";

import { NzBadgeModule } from "ng-zorro-antd/badge";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";

import { XwaiV2StreamService } from "../stream/xwai-v2-stream.service";
import { Conversation } from "../xwai.types";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { CdkNoDataRow } from "@angular/cdk/table";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import { UserService } from "../../shared/services/user.service";
import { XwaiModalComponent } from "../xwai-modal/xwai-modal.component";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { XwaiGuideComponent } from "../xwai-guide/xwai-guide.component";
import { XwaiImageFlipperComponent } from "../xwai-image-flipper/xwai-image-flipper.component";
import { ImageFlipperService } from "../../copilot/image-flipper.service";
import { NzSpinComponent } from "ng-zorro-antd/spin";

/**
 * Conversation sidebar: the logo row, New chat, and the Recent list.
 *
 * Two structural notes, both consequences of wrapping <nz-sider> in a component:
 *
 * - The host is `display: contents` (see the .less), so nz-sider remains the direct
 *   flex child of the shell's <nz-layout> rather than sitting inside a wrapper box.
 * - nz-layout finds its sider with a ContentChildren query, which cannot see into
 *   this component's view, so the shell carries `ant-layout-has-sider` itself.
 *
 * Collapsing stays ng-zorro's: nz-sider drives the inline nz-menu it contains, which
 * only works because both are declared in this one template.
 */
@Component({
  selector: "app-xwai-sidebar",
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    NzBadgeModule,
    NzButtonModule,
    NzCheckboxModule,
    NzCollapseModule,
    NzDrawerModule,
    NzDropDownModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzLayoutModule,
    NzMenuModule,
    NzTooltipModule,
    NzPopconfirmModule,
    XwaiGuideComponent,
    XwaiImageFlipperComponent,
    XwaiModalComponent,
    NzSpinComponent
  ],
  templateUrl: "./xwai-sidebar.component.html",
  styleUrls: ["./xwai-sidebar.component.less"],
})
export class XwaiSidebarComponent {
  /** Owned here and toggled by the trigger in the logo row; two-way bindable. */
  readonly collapsed = model(false);
  protected guideOpen = signal(false);
  private notification = inject(NzNotificationService);

  // Locks the rail to a specific collapsed state and hides its own trigger —
  // used when this sidebar is embedded in the floating model-ai-panel, which
  // drives collapse/expand from its own single expand button (via the
  // shell's `embedded`/`expanded` inputs) rather than letting this rail be
  // independently toggled. `null` (the standalone /xwai-v2 page) leaves the
  // rail on its own manual collapse trigger.
  readonly lockedCollapsed = input<boolean | null>(null);

  /** Whether an ancestor has taken control of collapse state (trigger hidden). */
  readonly isLocked = computed(() => this.lockedCollapsed() !== null);

  /** What the rail actually renders as — a lock wins over the manual toggle. */
  readonly isCollapsed = computed(() => this.lockedCollapsed() ?? this.collapsed());

  // Root-scoped: the same instance the stream and input components share.
  private service = inject(XwaiV2StreamService);
  private userService = inject(UserService)
  private imageFlipper = inject(ImageFlipperService);

  /** Pinned-image count, badged on the toggle below. */
  readonly imageCount = toSignal(
    this.imageFlipper.images$.pipe(map((images) => images.length)),
    { initialValue: 0 }
  );

  /** Opens/closes the same flipper drawer instance the FAB controls. */
  protected toggleImageFlipper(): void {
    this.imageFlipper.toggleDrawer();
  }

  private readonly currentUserEmail = toSignal(
    this.userService.getUserDetails().pipe(map((d): string | null => d?.email ?? null)),
    { initialValue: null as string | null }
  );

  readonly conversations = this.service.conversations;
  readonly threadId = this.service.threadId;
  readonly activeConversation = this.service.activeConversation
  readonly messages = this.service.messages;
  readonly loadingConversations = this.service.loadingConversations
  readonly unsavedVizEdits = this.service.unsavedVizEdits;

  /**
   * Visualization tweaks (crop, colormap, iteration, limits…) live in the message
   * tree and only reach the backend on save/update, so the tooltips call that out
   * while there are pending edits.
   */
  protected readonly saveTooltip = computed(() =>
    this.unsavedVizEdits()
      ? "Visualization settings changed — save to keep them"
      : "Save conversation"
  );
  protected readonly updateTooltip = computed(() =>
    this.unsavedVizEdits()
      ? "Visualization settings changed — update to save them"
      : "Update conversation"
  );

  /** This user's own conversations — shown in "Recent". */
  readonly myConversations = computed(() =>
    this.conversations().filter((c) => c.owner === this.currentUserEmail())
  );

  /** Everyone else's conversations — shown in "Public". */
  readonly otherConversations = computed(() =>
    this.conversations().filter((c) => c.owner !== this.currentUserEmail())
  );

  protected async setActiveConvo(convo: Conversation){
    this.service.setActiveConvo(convo)
  }
  protected startNewChat(){
    this.service.initSession()
    this.notification.success("Success", "Started a new conversation");
  }

  protected deleteConversation(convo: Conversation){
    this.service.deleteConverstaion(convo.id)
  }

  // ── Save / update conversation ───────────────────────────────────────────
  private fb = inject(FormBuilder);
  protected saveModalVisible = model<boolean>(false);

  /** Save-conversation form: name is required, description is optional, isPublic defaults to false. */
  protected saveForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
    isPublic: [false],
  });

  protected openSaveModal(): void {
    this.saveForm.reset({ name: '', description: '', isPublic: false });
    this.saveModalVisible.set(true);
  }

  /** Persist the current conversation from the form values. */
  protected save(): void {
    if (this.messages().length <= 1) {
      return
    }
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }
    const { name, description, isPublic } = this.saveForm.getRawValue();
    this.service.saveConversation(name, description, isPublic).then((convo) => {
      if (convo) {
        this.activeConversation.set(convo);
      }
      this.saveModalVisible.set(false);
    });
  }

  protected update(convo: Conversation): void {
    this.service.updateConversation(convo)
  }


  // ── Edit conversation modal ──────────────────────────────────────────────
  protected readonly editModalVisible = signal(false);
  protected editName = "";
  protected editDescription = "";
  protected editPublic = false;
  private editingConvoId: string | null = null;

  protected openEditModal(convo: Conversation): void {
    this.editingConvoId = convo.id;
    this.editName = convo.name;
    this.editDescription = convo.description;
    this.editPublic = convo.public;
    this.editModalVisible.set(true);
  }

  protected async saveEditedConversation(): Promise<void> {
    const convo = this.conversations().find((c) => c.id === this.editingConvoId);
    if (!convo || !this.editName.trim()) return;
    await this.service.updateConversation({
      ...convo,
      name: this.editName.trim(),
      description: this.editDescription,
      public: this.editPublic,
    });
    this.editModalVisible.set(false);
  }
}
